import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { StatusTag } from "../../components/common/registerUtils";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_ASSET_TRANSFERS,
  API_ASSET_TRANSFERS_EXPORT,
} from "../../utils/apis";
import {
  CAN_APPROVE_ASSET_TRANSFER,
  CAN_TRANSFER_ASSETS,
} from "../../utils/constants";
import {
  createViewAction,
  formatDate,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_ASSET_TRANSFERS } from "../../utils/urls";
import {
  APPROVAL_STATUSES,
  ASSET_CONDITIONS,
  AssetSummary,
  approvalSection,
  assetExport,
  assetLabel,
  auditFields,
  canTransferAsset,
  createAssetApprovalActionForm,
  createdAtFilter,
  toFilterOptions,
  useAssetPermissions,
  useAssetPicker,
  useDistrictOptions,
  userFullName,
  useUserOptions,
} from "./assetUtils";

export const useAssetTransfers = ({ status }: { status?: string } = {}) => {
  const can = useAssetPermissions();
  // transfer responses carry location UUIDs only
  const districts = useDistrictOptions();
  const locationName = (id: string, name?: string) =>
    name ?? districts.names[id] ?? id;

  const tableColumns = () => [
    { id: "transfer_id", header: "Transfer ID" },
    {
      id: "asset",
      header: "Asset",
      filter: { type: "text", key: "asset" },
    },
    { id: "current_location", header: "From" },
    { id: "new_location", header: "To" },
    { id: "transfer_date", header: "Transfer Date" },
    { id: "receiving_officer", header: "Receiving Officer" },
    {
      id: "status",
      header: "Status",
      ...(status
        ? {}
        : {
            filter: {
              type: "select",
              options: toFilterOptions(APPROVAL_STATUSES),
            },
          }),
    },
    { id: "created_at", header: "Requested On", filter: createdAtFilter },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      asset: assetLabel(item),
      current_location: locationName(
        item?.current_location,
        item?.current_location_name,
      ),
      new_location: locationName(item?.new_location, item?.new_location_name),
      receiving_officer: userFullName(item?.receiving_officer_details),
      status: <StatusTag status={item?.status} />,
      created_at: formatDate(item?.created_at),
      actions: (
        <FormActions
          actions={[createViewAction(URL_ASSET_TRANSFERS, item?.id)]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Transfer Details",
        fields: {
          transfer_id: item?.transfer_id,
          asset: assetLabel(item),
          from: locationName(
            item?.current_location,
            item?.current_location_name,
          ),
          to: locationName(item?.new_location, item?.new_location_name),
          asset_condition:
            ASSET_CONDITIONS[item?.asset_condition] ?? item?.asset_condition,
          transfer_date: item?.transfer_date,
          reason: item?.reason,
          receiving_officer: userFullName(item?.receiving_officer_details),
          status: <StatusTag status={item?.status} />,
        },
      },
      approvalSection(item),
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Asset Transfer",
    tableColumns,
    tableData,
    viewData,
    activityFilter: "asset_transfers",
    formRoute: can(CAN_TRANSFER_ASSETS)
      ? getForm(URL_ASSET_TRANSFERS)
      : undefined,
    createTitle: "New Transfer",
    exportData: assetExport(API_ASSET_TRANSFERS_EXPORT, "asset_transfers"),
  };
};

export const useAssetTransfersForm = ({
  form,
  searchParams,
  updateFormValue,
}: TFormHookParams & { searchParams?: Record<string, string> } = {}) => {
  const { search: assetSearch, asset } = useAssetPicker(form?.asset);
  const districts = useDistrictOptions();
  const officers = useUserOptions();

  // preselect the asset when opened from the asset register
  useEffect(() => {
    if (searchParams?.asset) updateFormValue?.("asset", searchParams.asset);
  }, []);

  const blocked = asset && !canTransferAsset(asset);

  const sections: IFormSection[] = [
    {
      title: "Transfer Details",
      fields: [
        {
          field_name: "asset",
          label: "Asset",
          type: "select",
          search: assetSearch,
          props: { required: true, placeholder: "Search asset by name" },
        },
        {
          field_name: "asset_summary",
          type: "component",
          visible: !!asset,
          component: (
            <AssetSummary
              asset={asset}
              warning={
                blocked
                  ? "Assets with this status cannot be transferred."
                  : undefined
              }
            />
          ),
        },
        {
          field_name: "new_location",
          label: "New Location",
          type: "select",
          // the destination must differ from where the asset is now
          options_data: districts.options.filter(
            (district: any) => district.value !== asset?.location,
          ),
          loading: districts.loading,
          props: { required: true, placeholder: "Select new location" },
        },
        {
          field_name: "transfer_date",
          label: "Transfer Date",
          type: "date",
          props: { required: true, disableFuture: true },
        },
        {
          field_name: "receiving_officer",
          label: "Receiving Officer",
          type: "select",
          search: officers,
          props: { required: true, placeholder: "Search staff by first name" },
        },
        {
          field_name: "reason",
          label: "Reason",
          type: "textarea",
          props: { required: true, placeholder: "Reason for the transfer" },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => ({
    api: API_ASSET_TRANSFERS,
    params: {
      asset: payload?.asset,
      new_location: payload?.new_location,
      transfer_date: payload?.transfer_date,
      reason: payload?.reason,
      receiving_officer: payload?.receiving_officer,
    },
  });

  return {
    sections,
    title: "Asset Transfer",
    redirect: getRegister(URL_ASSET_TRANSFERS),
    onSubmit,
    submitDisabled: !!blocked,
  };
};

export const useAssetTransferActionForm = createAssetApprovalActionForm(
  API_ASSET_TRANSFERS,
  CAN_APPROVE_ASSET_TRANSFER,
);
